import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { auth } from './firebase';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  OAuthProvider,
  signOut
} from 'firebase/auth';
import type { User } from 'firebase/auth';
import { useUsers } from '@/stores';

// SEC.2 T.3: auth-path debug logs gated behind VITE_DEBUG_LOGS so a
// production DevTools observer never sees the user's email/displayName/
// dev-mode chatter on the login or token paths.
const DEBUG = import.meta.env?.VITE_DEBUG_LOGS === '1';
const dlog = (...args: unknown[]) => { if (DEBUG) console.log(...args); };

/**
 * Fetch the user's Microsoft profile photo using the Graph API access token,
 * resize it to 200x200, and return a JPEG data URL. Returns null on any failure.
 */
async function fetchMicrosoftProfilePhoto(accessToken: string): Promise<string | null> {
  try {
    const res = await fetch('https://graph.microsoft.com/v1.0/me/photo/$value', {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (!res.ok) return null;

    const blob = await res.blob();
    return await new Promise<string | null>((resolve) => {
      const img = new Image();
      const objectUrl = URL.createObjectURL(blob);
      img.onload = () => {
        URL.revokeObjectURL(objectUrl);
        const size = 200;
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');
        if (!ctx) { resolve(null); return; }
        // Crop to square from center
        const side = Math.min(img.width, img.height);
        const sx = (img.width - side) / 2;
        const sy = (img.height - side) / 2;
        ctx.drawImage(img, sx, sy, side, side, 0, 0, size, size);
        resolve(canvas.toDataURL('image/jpeg', 0.85));
      };
      img.onerror = () => { URL.revokeObjectURL(objectUrl); resolve(null); };
      img.src = objectUrl;
    });
  } catch {
    return null;
  }
}

// PX.1 T.8 — `org_admin` + `fleet_admin` (the L2 delegated-admin tier) added to
// mirror the new pv_role values. The frontend guard mirrors the server gate
// (T.4); it NEVER replaces it — every guarded route's API is independently gated.
export type AppRole = 'admin' | 'developer' | 'org_admin' | 'fleet_admin' | 'lead' | 'production' | 'field_engineer' | 'contractor' | 'viewer' | 'verofy' | 'permitting' | 'permit_lead' | 'permit_staff' | 'pole_permit_lead' | 'pole_permit_staff' | 'other_permit_lead' | 'other_permit_staff' | 'unknown';
export type PermitRole = 'pole_permit_lead' | 'pole_permit_staff' | 'other_permit_lead' | 'other_permit_staff' | 'permit_lead' | 'permit_staff' | null;

type Ctx = {
  user: User | null;
  role: AppRole;
  permitRole: PermitRole;
  meId: number | null;
  weeklyPoleGoal: number | null;
  /** USEC.1 — gates Vault, MCP key surfaces, AI config, Security tab. */
  isSecurityAdmin: boolean;
  /**
   * PX.1 L1 — the caller's org id, for org-aware UI (org badge / switcher).
   * null == operator tier (admin/developer) or cross-org. DISPLAY ONLY — the
   * guard never enforces on this; the server does (T.1/T.2 org-filter + 403).
   */
  orgId: number | null;
  /** @deprecated Use `ready` for gating UI. Retained for back-compat. */
  loading: boolean;
  /** @deprecated Use `ready` for gating UI. Retained for back-compat. */
  roleVerified: boolean;
  /**
   * Phase UX1 single auth-resolution signal. True once we know enough to render:
   *  - Firebase auth has resolved (user is known or null), AND
   *  - For signed-in users: either a cached role exists (instant from localStorage)
   *    OR the authoritative `/me` fetch has resolved.
   * Stays true thereafter; never flips back to false on background refreshes.
   * See architecture/PHASE_UX1_LOAD_STATE_CONSOLIDATION_PLAN.md DL.4.
   */
  ready: boolean;
  /** True while a background `/me` refresh is in flight. UI consumers can use this for a subtle "refreshing" indicator. */
  isRefreshing: boolean;
  login: (email: string, password: string) => Promise<void>;
  loginWithMicrosoft: () => Promise<void>;
  logout: () => Promise<void>;
  getIdToken: () => Promise<string | undefined>;
};
const AuthCtx = createContext<Ctx>({} as any);

export const AuthProvider: React.FC<{children: React.ReactNode}> = ({ children }) => {
  const [user, setUser] = useState<User|null>(null);
  // Hydrate role synchronously from localStorage so the very first render after
  // a refresh already has a role for protected gates — avoids the "blank flash"
  // window between mount and onAuthStateChanged. See UX1 DL.8.
  const [role, setRole] = useState<AppRole>(() => {
    const cached = typeof window !== 'undefined' ? (localStorage.getItem('pvRole') as AppRole | null) : null;
    return cached || 'unknown';
  });
  const [permitRole, setPermitRole] = useState<PermitRole>(null);
  const [meId, setMeId] = useState<number | null>(null);
  const [weeklyPoleGoal, setWeeklyPoleGoal] = useState<number | null>(null);
  const [isSecurityAdmin, setIsSecurityAdmin] = useState(false);
  // PX.1 T.8 — the caller's org (L1), surfaced for org-aware UI (org badge /
  // switcher). null == operator tier / cross-org. The guard does NOT enforce on
  // this; the server does (T.1/T.2). Display only.
  const [orgId, setOrgId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [roleVerified, setRoleVerified] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    try {
      return onAuthStateChanged(auth, async (u) => {
        setUser(u);
        if (u) {
          // Unblock the UI immediately using cached role — don't wait for /me
          const cached = localStorage.getItem('pvRole') as AppRole | null;
          setRole(cached || 'unknown');
          setLoading(false);

          // Fetch authoritative role from backend; roleVerified gates role-sensitive redirects
          setIsRefreshing(true);
          ;(async () => {
            try {
              const token = await u.getIdToken();
              const response = await fetch(`${import.meta.env.VITE_API_URL}/me`, {
                headers: { 'Authorization': `Bearer ${token}`, 'X-Firebase-Auth': `Bearer ${token}` }
              });
              if (response.ok) {
                const userData = await response.json();
                setRole(userData.role as AppRole);
                setPermitRole((userData.permit_role as PermitRole) ?? null);
                setMeId(userData.id ?? null);
                setWeeklyPoleGoal(userData.weekly_pole_goal ?? null);
                setIsSecurityAdmin(userData.is_security_admin === true);
                setOrgId(userData.org_id ?? null);  // PX.1 T.8 — org-aware UI
                localStorage.setItem('pvRole', userData.role);

                // Hydrate the directory store so <UserAvatar /> can resolve
                // avatars and names by id across every portal. See
                // wiki/entities/user-avatars.md.
                useUsers.getState().hydrate(token).catch(() => { /* non-critical */ });

                // Auto-seed avatar from Firebase photoURL (cached Microsoft profile
                // photo) if none is saved in DB yet. The popup login path seeds from
                // the Graph API directly, but on page reload we only have the cached
                // Firebase photoURL. DeepSync-uploaded avatars are never overwritten.
                if (!userData.avatar_url && u.photoURL) {
                  (async () => {
                    try {
                      // Convert external URL to a self-contained data URL so the
                      // avatar works even if the external URL expires.
                      const res = await fetch(u.photoURL!);
                      if (!res.ok) return;
                      const blob = await res.blob();
                      const img = new Image();
                      const objUrl = URL.createObjectURL(blob);
                      img.onload = () => {
                        URL.revokeObjectURL(objUrl);
                        const size = 200;
                        const canvas = document.createElement('canvas');
                        canvas.width = size;
                        canvas.height = size;
                        const ctx = canvas.getContext('2d');
                        if (!ctx) return;
                        const side = Math.min(img.width, img.height);
                        ctx.drawImage(img, (img.width - side) / 2, (img.height - side) / 2, side, side, 0, 0, size, size);
                        const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
                        fetch(`${import.meta.env.VITE_API_URL}/me`, {
                          method: 'PATCH',
                          headers: { 'Authorization': `Bearer ${token}`, 'X-Firebase-Auth': `Bearer ${token}`, 'Content-Type': 'application/json' },
                          body: JSON.stringify({ avatar_url: dataUrl }),
                        }).catch(() => {});
                      };
                      img.onerror = () => URL.revokeObjectURL(objUrl);
                      img.src = objUrl;
                    } catch { /* non-critical */ }
                  })();
                }
              } else {
                const tokenResult = await u.getIdTokenResult();
                const r = ((tokenResult.claims.role as AppRole) || cached || 'production') as AppRole;
                setRole(r);
                localStorage.setItem('pvRole', r);
              }
            } catch {
              // Keep cached role — backend unreachable, no user impact
            } finally {
              setRoleVerified(true);
              setIsRefreshing(false);
            }
          })();
        } else {
          setRole('unknown');
          setPermitRole(null);
          setMeId(null);
          setRoleVerified(true);
          localStorage.removeItem('pvRole');
          useUsers.getState().clear();
          setLoading(false);
        }
      });
    } catch (error) {
      // SOC2-1.1: dev-mode fallback removed. If Firebase init fails in prod,
      // the app fails closed — no silent admin grant via dev-token path.
      console.error('Firebase auth state change error:', error);
      setLoading(false);
    }
  }, []);

  // Keep the directory store fresh while the user is signed in. We refetch on
  // window focus, on visibility change to "visible", and every 5 min while
  // visible. The store throttles itself (default 30s) so multiple triggers in
  // quick succession collapse into one fetch. This catches admin-side changes
  // to other users (renames, role changes, deactivations) without requiring a
  // page refresh — the same hole the avatar regression exposed for names/roles.
  useEffect(() => {
    if (!user) return;

    const refresh = async () => {
      try {
        const token = await user.getIdToken();
        if (!token) return;
        await useUsers.getState().refresh(token);
      } catch { /* non-critical */ }
    };

    const onFocus = () => { refresh(); };
    const onVisibility = () => { if (document.visibilityState === 'visible') refresh(); };

    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    const interval = window.setInterval(() => {
      if (document.visibilityState === 'visible') refresh();
    }, 5 * 60 * 1000);

    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
      window.clearInterval(interval);
    };
  }, [user]);

  const login = async (email: string, password: string) => {
    // SOC2-1.1: dev-mode bypass removed. All callers must auth via Firebase.
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Get token and fetch role from backend (source of truth)
      const token = await user.getIdToken();

      try {
        const response = await fetch(`${import.meta.env.VITE_API_URL}/me`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-Firebase-Auth': `Bearer ${token}`
          }
        });
        
        if (response.ok) {
          const userData = await response.json();
          setRole(userData.role as AppRole);
          setPermitRole((userData.permit_role as PermitRole) ?? null);
          setWeeklyPoleGoal(userData.weekly_pole_goal ?? null);
          setIsSecurityAdmin(userData.is_security_admin === true);
          localStorage.setItem('pvRole', userData.role);
        } else {
          // Backend unreachable — use cached role, default to production (never admin)
          const fallback = (localStorage.getItem('pvRole') as AppRole) || 'production';
          setRole(fallback);
        }
      } catch (backendError) {
        console.error('Error fetching role from backend:', backendError);
        const fallback = (localStorage.getItem('pvRole') as AppRole) || 'production';
        setRole(fallback);
      } finally {
        setRoleVerified(true);
      }

    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const loginWithMicrosoft = async () => {
    dlog('[AuthProvider] Microsoft login attempt');
    // SOC2-1.1: dev-mode bypass removed. All callers must auth via Firebase.
    try {
      // Configure Microsoft provider
      const provider = new OAuthProvider('microsoft.com');
      
      // Optional: Request specific scopes
      provider.addScope('email');
      provider.addScope('profile');
      provider.addScope('openid');
      
      // Set tenant ID if you want to restrict to your organization
      const tenantId = import.meta.env.VITE_MICROSOFT_TENANT_ID;
      if (tenantId) {
        provider.setCustomParameters({
          tenant: tenantId,
        });
      }
      
      // Sign in with popup
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      
      dlog('[AuthProvider] Microsoft login successful:', {
        email: user.email,
        displayName: user.displayName
      });

      // Get token and fetch role from backend (source of truth)
      const token = await user.getIdToken();

      try {
        const response = await fetch(`${import.meta.env.VITE_API_URL}/me`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-Firebase-Auth': `Bearer ${token}`
          }
        });
        
        if (response.ok) {
          const userData = await response.json();
          setRole(userData.role as AppRole);
          setPermitRole((userData.permit_role as PermitRole) ?? null);
          setWeeklyPoleGoal(userData.weekly_pole_goal ?? null);
          setIsSecurityAdmin(userData.is_security_admin === true);
          localStorage.setItem('pvRole', userData.role);

          // Auto-seed avatar from Microsoft profile photo if none is saved yet
          if (!userData.avatar_url) {
            const credential = OAuthProvider.credentialFromResult(result);
            const accessToken = credential?.accessToken;
            const photoDataUrl = accessToken
              ? await fetchMicrosoftProfilePhoto(accessToken)
              : null;
            const photoToSave = photoDataUrl || user.photoURL || null;
            if (photoToSave) {
              fetch(`${import.meta.env.VITE_API_URL}/me`, {
                method: 'PATCH',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'X-Firebase-Auth': `Bearer ${token}`,
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({ avatar_url: photoToSave }),
              }).catch(() => { /* non-critical */ });
            }
          }
        } else {
          const fallback = (localStorage.getItem('pvRole') as AppRole) || 'production';
          setRole(fallback);
        }
      } catch (backendError) {
        console.error('Error fetching role from backend:', backendError);
        const fallback = (localStorage.getItem('pvRole') as AppRole) || 'production';
        setRole(fallback);
      } finally {
        setRoleVerified(true);
      }
      
    } catch (error: any) {
      console.error('Microsoft login error:', error);
      
      // Handle specific errors
      if (error.code === 'auth/popup-closed-by-user') {
        throw new Error('Sign-in popup was closed');
      } else if (error.code === 'auth/cancelled-popup-request') {
        throw new Error('Sign-in was cancelled');
      } else if (error.code === 'auth/account-exists-with-different-credential') {
        // Account exists with email/password, need to link accounts
        console.log('[AuthProvider] Account exists with different credential - attempting to provide helpful error');
        throw new Error(
          'This email is already registered with a password. Please sign in with your email and password first, ' +
          'or contact your administrator to migrate your account to Microsoft sign-in.'
        );
      } else if (
        error.code === 'auth/invalid-credential' ||
        error.code === 'auth/invalid-oauth-provider' ||
        error.code === 'auth/invalid-tenant-id' ||
        error.code === 'auth/unauthorized-domain' ||
        error.code === 'auth/operation-not-allowed' ||
        error.code === 'auth/internal-error'
      ) {
        // Server-side / provider-config errors. Hide raw Azure AADSTS trace IDs +
        // app-registration UUIDs from end users; full error is in console for ops.
        throw new Error(
          'Microsoft sign-in is temporarily unavailable. Please try again in a few minutes or contact your administrator.'
        );
      } else if (error.code === 'auth/network-request-failed') {
        throw new Error('Network error. Please check your connection and try again.');
      }

      // Catch-all: never expose raw OAuth provider details to the UI surface.
      // Operator gets full details via the console.error above (line 384).
      throw new Error('Sign-in failed. Please try again or contact support.');
    }
  };
  
  const logout = async () => {
    // Phase UX1: clear cached role synchronously BEFORE firebase signOut fires,
    // so a user-switch on the same browser cannot momentarily carry the
    // previous user's role through the optimistic cached-role render. The
    // onAuthStateChanged handler also clears, but that path is async.
    localStorage.removeItem('pvRole');
    setRole('unknown');
    setIsSecurityAdmin(false);

    // SOC2-1.1: dev-mode bypass removed. Always go through Firebase signOut.
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  };

  const getIdToken = async () => {
    // SOC2-1.1: dev-mode 'dev-token' literal removed. Return a real Firebase
    // ID token only — every call site must handle undefined gracefully (e.g.
    // surface a login prompt rather than smuggling a server-trusted shortcut).
    try {
      return await auth.currentUser?.getIdToken();
    } catch (error) {
      console.error('Get ID token error:', error);
      return undefined;
    }
  };

  // Phase UX1 single auth-resolution signal. Becomes true the moment we know
  // enough to render the destination route — replaces the legacy split-state
  // `loading || (user && !roleVerified)` pattern that flashed two spinners
  // back-to-back. Stays true once flipped; background refreshes use
  // `isRefreshing` for a subtle indicator instead of re-gating the whole UI.
  const hasCachedRole = role !== 'unknown';
  const ready = !loading && (user === null || roleVerified || hasCachedRole);

  // Hide the index.html cold-boot splash (#ds-splash) the moment auth resolves.
  // Keeping it up until `ready` prevents a blank-screen flash between React's
  // first paint and the gate components getting enough state to render. The
  // rAF-based hide in main.tsx is retained as a fallback for edge cases (e.g.
  // Firebase init failure where AuthProvider never reaches ready=true).
  useEffect(() => {
    if (!ready || typeof document === 'undefined') return;
    const splash = document.getElementById('ds-splash');
    if (splash && !splash.classList.contains('hidden')) {
      splash.classList.add('hidden');
      window.setTimeout(() => splash.remove(), 350);
    }
  }, [ready]);

  const value = useMemo(
    () => ({ user, role, permitRole, meId, weeklyPoleGoal, isSecurityAdmin, orgId, loading, roleVerified, ready, isRefreshing, login, loginWithMicrosoft, logout, getIdToken }),
    [user, role, permitRole, meId, weeklyPoleGoal, isSecurityAdmin, orgId, loading, roleVerified, ready, isRefreshing]
  );
  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
};
export const useAuth = () => useContext(AuthCtx);
/** Convenience hook for surfaces that want a "refreshing your profile…" toast. */
export const useRefreshingRole = () => useAuth().isRefreshing;
